import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import UserAccessList from "./UserAccessList";
import AccessLevelTabs from "./AccessLevelTabs";
import type { AccessLevel } from "../types";
import { normalizeSharingUsers } from "../adapters";
import type { MCPSharing } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  sharing: MCPSharing | undefined | null;
};

const AccessLevelSection = ({ connectionId, sharing }: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ChevronIcon = open ? ChevronDown : ChevronRight;

  const isUserScope = sharing?.scope === "user";

  const users = useMemo(
    () =>
      isUserScope
        ? normalizeSharingUsers((sharing as { items?: unknown }).items as never)
        : [],
    [isUserScope, sharing],
  );

  const projectName =
    sharing?.scope === "project" ? sharing.name?.trim() : "";
  const projectValue =
    sharing?.scope === "project" ? projectName || sharing.item : "";

  const scope: AccessLevel = sharing?.scope === "project" || sharing?.scope === "user"
    ? sharing.scope
    : "organization";

  return (
    <section className="border-t border-border px-4 py-4">
      <h3 className="mb-1 text-xs font-medium text-secondary-foreground">{t("mcpPage.fields.accessLevel")}</h3>
      <AccessLevelTabs value={scope} readOnly />

      {isUserScope ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="mt-3 flex w-full cursor-pointer items-center gap-2 py-2 text-sm text-foreground"
            aria-expanded={open}
          >
            <ChevronIcon className={`size-4 text-muted-foreground ${i18n.dir() === "rtl" && !open ? "rotate-180" : ""}`} />
            <span>{t("mcpPage.accessScope.selectedUsers")}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {users.length}
            </span>
          </button>
          {open && (
            <UserAccessList connectionId={connectionId} users={users} />
          )}
        </>
      ) : scope === "project" ? (
        <div className="mt-2">
          <span className="mb-1 block text-xs font-medium text-secondary-foreground">
            {t("mcpPage.fields.project")}
          </span>
          <div className="flex h-9 w-full items-center justify-between rounded-[8px] border border-border bg-card px-3 text-sm text-foreground">
            <span className="truncate">
              {projectValue || t("mcpPage.accessScope.selectedProject")}
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AccessLevelSection;
